import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

// One Socket.IO connection to the /attendance namespace per browser tab.
//
// The server decides what a socket receives from the JWT: an agent token only
// echoes that agent's own transitions (keeps a second tab in sync), an admin
// token receives every agent's. The admin dashboard is driven by this feed;
// the REST snapshot is only what it starts from.

const socketUrl = () =>
  (process.env.REACT_APP_ATTENDANCE_SOCKET_URL ||
    process.env.REACT_APP_JUSTTAWK_IND_BASE_API_URL ||
    "").replace(/\/+$/, "");

let socket = null;
let tokenUsed = null;

export const getAttendanceSocket = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  if (socket && tokenUsed === token) return socket;
  if (socket) socket.close();
  tokenUsed = token;
  socket = io(`${socketUrl()}/attendance`, {
    path: process.env.REACT_APP_ATTENDANCE_SOCKET_PATH || "/socket.io",
    auth: { token },
    transports: ["websocket", "polling"],
    reconnectionDelayMax: 10000,
  });
  return socket;
};

export const closeAttendanceSocket = () => {
  if (socket) socket.close();
  socket = null;
  tokenUsed = null;
};

/**
 * Subscribe to attendance events for the lifetime of the component.
 * `onReconnect` fires after a dropped connection comes back so the screen can
 * re-fetch -- events sent while offline are gone, the snapshot is not.
 */
export const useAttendanceEvents = (events, handler, onReconnect) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const reconnectRef = useRef(onReconnect);
  reconnectRef.current = onReconnect;

  const key = [].concat(events).join("|");

  useEffect(() => {
    const s = getAttendanceSocket();
    if (!s) return undefined;

    const bound = key.split("|").map((event) => {
      const fn = (payload) => handlerRef.current(payload, event);
      s.on(event, fn);
      return [event, fn];
    });

    const reconnect = () => reconnectRef.current && reconnectRef.current();
    s.on("reconnect", reconnect);

    return () => {
      bound.forEach(([event, fn]) => s.off(event, fn));
      s.off("reconnect", reconnect);
    };
  }, [key]);
};
